import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/ui/Textarea';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { staffAttendanceApi } from '../api/staffAttendance.api';
import { ATTENDANCE_NOTE_MAX_LENGTH, attendanceNoteSchema } from '../schemas/attendanceNote.schema';

/**
 * Writing, changing or clearing the office's note on one member of staff's day.
 *
 * One box for all three: saving it empty removes the note, so there is no
 * separate delete to find. The note is the office's alone — the member of
 * staff never sees it — which the description says, so nobody writes it as a
 * message to them.
 */

export interface AttendanceNoteTarget {
  /** The member of staff's account uuid — a day needs no punch to carry a note. */
  staffUuid: string;
  /** `YYYY-MM-DD`. */
  date: string;
  staffName: string;
  /** "Mon, 13 Oct" — whose day this is about. */
  dayLabel: string;
  note: string | null;
}

export interface AttendanceNoteModalProps {
  target: AttendanceNoteTarget | null;
  onClose: () => void;
  /** The register re-reads, so the saved note shows in its row. */
  onSaved: () => void;
}

export function AttendanceNoteModal({ target, onClose, onSaved }: AttendanceNoteModalProps) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  // Each opening starts from that day's note, not the last one typed.
  useEffect(() => {
    setDraft(target?.note ?? '');
    setError(undefined);
  }, [target]);

  function close() {
    if (!isSaving) onClose();
  }

  async function save() {
    if (!target) return;

    const parsed = attendanceNoteSchema.safeParse(draft);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message);
      return;
    }

    setIsSaving(true);
    try {
      const saved = await staffAttendanceApi.setNote(target.staffUuid, target.date, parsed.data);
      toast.success(saved.note ? 'Note saved' : 'Note removed', `${target.staffName} · ${target.dayLabel}`);
      onSaved();
      setIsSaving(false);
      onClose();
    } catch (caught) {
      setIsSaving(false);
      toast.error(
        'Could not save the note',
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
    }
  }

  const unchanged = draft.trim() === (target?.note ?? '');

  return (
    <Modal
      isOpen={target !== null}
      onClose={close}
      title={target?.note ? 'Edit note' : 'Add note'}
      description={
        target
          ? `${target.staffName} · ${target.dayLabel}. Only the office sees this note.`
          : undefined
      }
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={() => void save()} isLoading={isSaving} disabled={unchanged}>
            {target?.note && !draft.trim() ? 'Remove note' : 'Save note'}
          </Button>
        </>
      }
    >
      <Textarea
        label="Note"
        rows={3}
        value={draft}
        maxLength={ATTENDANCE_NOTE_MAX_LENGTH}
        placeholder="For example: left early for a hospital visit"
        error={error}
        hint={`${draft.length}/${ATTENDANCE_NOTE_MAX_LENGTH} characters. Leave it empty to remove the note.`}
        onChange={(event) => {
          setDraft(event.target.value);
          setError(undefined);
        }}
        disabled={isSaving}
      />
    </Modal>
  );
}
